import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {Box, Button, Divider, IconButton, Tooltip} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useMemo, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {ExportField} from '../../../interfaces/Export';
import {HazardAIMachineSummaryQuery} from '../../../interfaces/HazardAIMachineSummary';
import {saveFile} from '../../../utils/file';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';
import ExportFieldForm from '../../common/ExportFieldForm';
import ModalFixed from '../../common/ModalFixed';
import SnackbarMessages from '../../common/SnackbarMessages';

/**
 * The canonical list of exportable fields for the HazardAI Machine Summary
 * grid. Aligned to the backend's `hazardMachineSummaryExportFields` whitelist
 * in `SummaryGrid.ts`. Other HazardAI reports that include HazardAISummaryGrid
 * can import this constant and pass it (with their own shownFields) to
 * HazardAISummaryGridExportButton.
 */
export const HAZARD_AI_MACHINE_SUMMARY_EXPORT_FIELDS: ExportField[] = [
  {field: 'safeye_node_id', label: 'Node ID'},
  {field: 'machine_name', label: 'Machine Name'},
  {field: 'site_name', label: 'Site'},
  {field: 'machine_type', label: 'Machine Type'},
  {field: 'hazard_person', label: 'Hazard Person'},
  {field: 'warning_person', label: 'Warning Person'},
  {field: 'id_person', label: 'ID Person'},
  {field: 'hazard_vehicle', label: 'Hazard Vehicle'},
  {field: 'warning_vehicle', label: 'Warning Vehicle'},
  {field: 'id_vehicle', label: 'ID Vehicle'},
  {field: 'max_speed', label: 'Max Speed'},
  {field: 'operation_time', label: 'Operation Time'},
  {field: 'operating_no_movement', label: 'Operating with No Movement'},
  {field: 'idle_time', label: 'Idle Time'},
];

interface Props {
  params: HazardAIMachineSummaryQuery;
  shownFields: string[];
  filenamePrefix?: string;
}

const HazardAISummaryGridExportButton = ({
  params,
  shownFields,
  filenamePrefix = 'hazard_ai_machine_summary',
}: Props) => {
  const {enqueueSnackbar} = useSnackbar();
  const [isOpened, setIsOpened] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const exportFields = useMemo<ExportField[]>(
    () =>
      HAZARD_AI_MACHINE_SUMMARY_EXPORT_FIELDS.map((f) => ({
        ...f,
        hidden: !shownFields.includes(f.field),
      })),
    [shownFields]
  );

  const handleExport = async (fields: string[]) => {
    setIsOpened(false);
    setIsFetching(true);
    try {
      const resp = await API.get(
        `${apiBaseUrl}/hazard-ai/machine-summary-grid`,
        {
          params: {...params, export: 'excel', fields},
          responseType: 'blob',
        }
      );
      saveFile(
        resp.data,
        `${filenamePrefix}_${params.date_start ?? 'start'}__${params.date_end ?? 'end'}.xlsx`
      );
    } catch (error: any) {
      const messages = getMessagesFromApiError(error, 'HAIMUEB-73');
      enqueueSnackbar(<SnackbarMessages messages={messages} />, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }
    setIsFetching(false);
  };

  return (
    <>
      <Tooltip title="Export Machine Summary">
        <Button
          size="small"
          variant="outlined"
          disabled={isFetching}
          onClick={() => setIsOpened(true)}
        >
          <FileDownloadIcon />
        </Button>
      </Tooltip>

      {isOpened ? (
        <ModalFixed open={isOpened} onClose={() => setIsOpened(false)}>
          <Box
            display="flex"
            flexDirection="column"
            position="relative"
            gap={3}
            p={3}
          >
            <Box display="flex" justifyContent="space-between">
              <Box display="flex" alignItems="center" width="100%" gap={1.5}>
                <FileDownloadIcon sx={{color: 'primary.main'}} />
                <Box fontSize={24}>Export Machine Summary</Box>
              </Box>
              <IconButton onClick={() => setIsOpened(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              position="relative"
              gap={3}
            >
              <Divider>Export</Divider>
              <ExportFieldForm
                exportFields={exportFields}
                loading={isFetching}
                onChange={handleExport}
                close={() => setIsOpened(false)}
                columns={3}
              />
            </Box>
          </Box>
        </ModalFixed>
      ) : null}
    </>
  );
};

export default HazardAISummaryGridExportButton;
